"use client";

import type { JSX } from "react";
import { useState } from "react";

type FormType = "feedback" | "question" | "sponsoring";

interface ValidationErrors {
  name?: string;
  email?: string;
  message?: string;
}

export default function ContactForm(): JSX.Element {
  const [formType, setFormType] = useState<FormType>("feedback");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [submitStatus, setSubmitStatus] = useState<string>("");

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    } else if (formData.message.trim().length < 10) {
      newErrors.message = "Message must be at least 10 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault(); // Prevent default form submission

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitStatus("sending");
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...formData,
          type: formType,
        }),
      });

      if (response.ok) {
        setSubmitStatus("success");
        setFormData({ name: "", email: "", message: "" });
        setTimeout(() => setSubmitStatus(""), 3000); // Clear status after 3 seconds
      } else {
        const error = await response.json();
        setSubmitStatus("error");
        console.error("Failed to send message:", error);
      }
    } catch (error) {
      setSubmitStatus("error");
      console.error("An error occurred:", error);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    // Clear error when user starts typing
    if (errors[name as keyof ValidationErrors]) {
      setErrors({ ...errors, [name]: undefined });
    }
  };

  return (
    <div className="mt-12 space-y-8">
      {/* Form Type Selection */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Contact Type
        </label>
        <div className="grid grid-cols-3 gap-2">
          {[
            { id: "feedback", label: "Feedback" },
            { id: "question", label: "Question" },
            { id: "sponsoring", label: "Sponsoring" },
          ].map((type) => (
            <button
              key={type.id}
              type="button"
              onClick={() => setFormType(type.id as FormType)}
              className={`py-2 px-4 rounded-lg border ${
                formType === type.id
                  ? "bg-indigo-600 text-white border-indigo-600"
                  : "bg-white text-gray-900 border-gray-200 hover:bg-gray-50"
              }`}
            >
              {type.label}
            </button>
          ))}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-2">
          <label
            htmlFor="name"
            className="block text-sm font-medium text-gray-700"
          >
            Name
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            className={`block w-full rounded-lg border shadow-sm focus:border-indigo-500 focus:ring-indigo-500 ${
              errors.name ? "border-red-500" : "border-gray-300"
            }`}
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-600">{errors.name}</p>
          )}
        </div>

        <div className="space-y-2">
          <label
            htmlFor="email"
            className="block text-sm font-medium text-gray-700"
          >
            Email
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            className={`block w-full rounded-lg border shadow-sm focus:border-indigo-500 focus:ring-indigo-500 ${
              errors.email ? "border-red-500" : "border-gray-300"
            }`}
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-600">{errors.email}</p>
          )}
        </div>

        <div className="space-y-2">
          <label
            htmlFor="message"
            className="block text-sm font-medium text-gray-700"
          >
            Message
          </label>
          <textarea
            id="message"
            name="message"
            rows={5}
            value={formData.message}
            onChange={handleInputChange}
            className={`block w-full rounded-lg border shadow-sm focus:border-indigo-500 focus:ring-indigo-500 ${
              errors.message ? "border-red-500" : "border-gray-300"
            }`}
          />
          {errors.message && (
            <p className="mt-1 text-sm text-red-600">{errors.message}</p>
          )}
        </div>

        <div className="flex justify-center pt-6">
          <button
            type="submit"
            disabled={submitStatus === "sending"}
            className={`rounded-lg px-8 py-3 text-base font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 ${
              submitStatus === "sending"
                ? "bg-indigo-400 cursor-not-allowed"
                : "bg-indigo-600 hover:bg-indigo-500"
            }`}
          >
            {submitStatus === "sending" ? "Sending..." : "Send Message"}
          </button>
        </div>

        {submitStatus === "success" && (
          <p className="text-center text-green-600">
            Message sent successfully!
          </p>
        )}
        {submitStatus === "error" && (
          <p className="text-center text-red-600">
            Failed to send message. Please try again.
          </p>
        )}
      </form>
    </div>
  );
}
